import { api } from './api';
import type {
  PickupRequestPayload,
  PickupResponse,
  CreateOrdersPayload,
  CreateOrdersResponse,
  OrderListRequest,
  OrderListResponse,
  TrackingResponse,
} from '../types/orders';
import { publicApi } from './publicApi';

export interface ChannelAwbUpdatePayload {
  user_id: number;
  sOrder_id: number | string;
  awb: string;
}

/**
 * Extract shipment IDs from pickup API response.
 * API returns top-level shipment_id (single) or arrays: shipment_ids, shipmentIds, shipments.
 */
export function getShipmentIdsFromPickupResponse(
  res: PickupResponse
): number[] {
  if (typeof res.shipment_id === 'number') return [res.shipment_id];
  if (Array.isArray(res.shipment_ids)) return res.shipment_ids;
  if (Array.isArray(res.shipmentIds)) return res.shipmentIds;
  if (Array.isArray(res.shipments)) {
    return (res.shipments as { shipment_id?: number; id?: number }[])
      .map((s) => s.shipment_id ?? s.id)
      .filter((id): id is number => typeof id === 'number');
  }
  return [];
}

export const ordersService = {
  /**
   * Create a pickup request
   */
  async createPickup(
    userId: string | number,
    body: PickupRequestPayload
  ): Promise<PickupResponse> {
    const { data } = await api.post<PickupResponse>(
      `/users/${userId}/pickup?shipment=true`,
      body
    );
    return data;
  },

  async getB2bRates(
    userId: string | number,
    body: any
  ): Promise<any> {
    const { data } = await api.post(
      `/b2b/users/${userId}/rates`,
      body
    );
    return data;
  },

  async createB2bPickup(
    userId: string | number,
    formData: FormData
  ): Promise<any> {
    const { data } = await api.post(
      `/b2b/users/${userId}/create`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  async scheduleArchivedB2bShipment(
    userId: string | number,
    shipmentId: string | number,
    courierId?: number
  ): Promise<any> {
    const { data } = await api.post(
      `/b2b/shipment/${userId}/schedule-archive/${shipmentId}`,
      { courierId },
      { params: courierId ? { courierId } : {} }
    );
    return data;
  },

  async getB2bOrderDetails(
    userId: string | number,
    shipmentId: string | number,
    roleId?: string | number
  ): Promise<any> {
    const { data } = await api.get(
      `/b2b/users/${userId}/shipments/${shipmentId}`,
      { headers: roleId ? { roleId: roleId.toString() } : {} }
    );
    return data;
  },

  async getB2bOrders(
    userId: string | number,
    roleId?: string | number,
    params?: {
      tab?: string;
      search?: string;
      paymentType?: string;
      courierId?: number | null;
      shippingMode?: string;
      page?: number;
      pageSize?: number;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<any> {
    const { data } = await api.get(
      `/b2b/users/${userId}/shipments`,
      { 
        headers: roleId ? { roleId: roleId.toString() } : {},
        params
      }
    );
    return data;
  },
  
  async cancelB2bOrder(userId: string | number, shipmentId: string, reason?: string) {
    const { data } = await api.post(
      `/b2b/users/${userId}/cancel`,
      { shipmentId, reason }
    );
    return data;
  },

  async exportB2bAppointments(startDate: string, endDate: string) {
    const res = await api.get('/b2b/appointments/export', {
      params: { startDate, endDate },
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Appointments_${startDate}_to_${endDate}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  async exportB2bShipmentReport(
    userId: string | number,
    roleId?: string | number,
    startDate?: string,
    endDate?: string
  ) {
    const res = await api.get(`/b2b/report/shipments/${userId}/excel`, {
      params: { startDate, endDate },
      headers: roleId ? { roleId: roleId.toString() } : {},
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `B2B_Shipment_Report_${startDate || 'all'}_to_${endDate || 'all'}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  async assignShipmentToUser(shipmentId: string | number, userId: string | number, roleId: string | number) {
    const { data } = await api.post(
      `/b2b/shipments/${shipmentId}/assign`,
      { userId },
      { headers: { roleId: roleId.toString() } }
    );
    return data;
  },

  /**
   * Confirm orders with shipment IDs – POST /orders
   * Body: { shipmentIds: number[] }
   */
  async createOrders(body: CreateOrdersPayload): Promise<CreateOrdersResponse> {
    const { data } = await api.post<CreateOrdersResponse>('/orders', body);
    return data;
  },

  /**
   * Get list of orders with filtering
   */
  async getOrders(
    userId: string | number,
    body: OrderListRequest
  ): Promise<OrderListResponse> {
    const { data } = await api.post<OrderListResponse>(
      `/channel/forward_orders/${userId}/shipments/lists`,
      body
    );
    return data;
  },

  async cancelOrder(
    shipmentId: string | number,
    userId: string | number
  ): Promise<any> {
    const { data } = await api.put(`/shipment/${shipmentId}/cancel/${userId}`);
    return data;
  },

  async updateChannelOrder(
    payload: any
  ): Promise<any> {
    const { data } = await api.put(`/channel/order/update`, payload);
    return data;
  },

  async getManifest(userId: string | number, payload: any): Promise<any> {
    const { data } = await api.post(
      `/shipment/${userId}/getmanifestzip`,
      payload
    );
    return data;
  },

  async getB2bManifest(userId: string | number, payload: any): Promise<any> {
    const { data } = await api.post(
      `/b2b/shipment/${userId}/getmanifestzip`,
      payload
    );
    return data;
  },

  async getB2bNotes(shipmentId: number | string, userId: string | number): Promise<any> {
    const { data } = await api.get(`/b2b/note/${shipmentId}`);
    return data;
  },

  async addB2bNote(shipmentId: number | string, userId: string | number, payload: { note: string }): Promise<any> {
    const { data } = await api.post(`/b2b/note/${shipmentId}`, payload);
    return data;
  },

  async updateB2bNote(noteId: number | string, userId: string | number, payload: { note: string }): Promise<any> {
    const { data } = await api.put(`/b2b/note/${noteId}`, payload);
    return data;
  },

  async deleteB2bNote(shipmentId: number | string, noteId: number | string): Promise<any> {
    const { data } = await api.delete(`/b2b/note/${shipmentId}/${noteId}`);
    return data;
  },

  async getZipLabels(userId: string | number, payload: any): Promise<any> {
    const { data } = await api.post(`/shipment/${userId}/getzipNew`, payload);
    return data;
  },

  async getTrackingDetails(trackingNumber: string): Promise<TrackingResponse> {
    const { data } = await publicApi.get<TrackingResponse>(
      `/track/${trackingNumber}`
    );
    return data;
  },

  /**
   * Update AWB for a channel order so it gets removed from the pending list
   */
  async updateChannelAWB(payload: ChannelAwbUpdatePayload): Promise<any> {
    const { data } = await api.post('/channel/awb/update', payload);
    return data;
  },
};
